"use client";

import { useId, useRef } from "react";
import { BUTTON_SMALL } from "@/components/tool-kit/styles";
import { downloadPng } from "@/lib/tool-kit/download";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

/*
 * A two-set Venn diagram with the elements written in each region. Long regions are cut
 * to a few lines with a "+N" tail so the picture stays legible; the tables beside it
 * have the full lists.
 */
const WIDTH = 480;
const HEIGHT = 270;
const PER_LINE = 3;
const MAX_LINES = 6;
const UNSAFE_ID = /[^a-zA-Z0-9_-]/g;

function lines(elements: readonly string[]): string[] {
  const rows: string[] = [];
  const capacity = PER_LINE * MAX_LINES;
  const shown =
    elements.length > capacity ? elements.slice(0, capacity - 1) : elements;
  for (let i = 0; i < shown.length; i += PER_LINE) {
    rows.push(shown.slice(i, i + PER_LINE).join(", "));
  }
  if (elements.length > capacity) {
    rows.push(`+${elements.length - shown.length}`);
  }
  return rows;
}

function Region({ x, elements }: { x: number; elements: readonly string[] }) {
  const rows = lines(elements);
  const top = 138 - ((rows.length - 1) * 15) / 2;
  return (
    <text fill={CHART_INK.text} fontSize={12} textAnchor="middle" x={x}>
      {rows.map((row, i) => (
        <tspan
          dy={i === 0 ? 0 : 15}
          key={`${i}-${row}`}
          x={x}
          y={i === 0 ? top : undefined}
        >
          {row}
        </tspan>
      ))}
    </text>
  );
}

export function VennDiagram({
  onlyA,
  both,
  onlyB,
  outside,
  showUniverse,
  title,
  downloadLabel,
}: {
  onlyA: readonly string[];
  both: readonly string[];
  onlyB: readonly string[];
  outside: readonly string[];
  showUniverse: boolean;
  title: string;
  downloadLabel: string;
}) {
  const ref = useRef<SVGSVGElement>(null);
  const uid = useId().replace(UNSAFE_ID, "");
  return (
    <div className="space-y-3">
      <svg
        aria-labelledby={`venn-${uid}`}
        className="h-auto w-full max-w-xl"
        fontFamily="system-ui, -apple-system, Segoe UI, sans-serif"
        ref={ref}
        role="img"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        xmlns="http://www.w3.org/2000/svg"
      >
        <title id={`venn-${uid}`}>{title}</title>
        <rect fill={CHART_INK.background} height={HEIGHT} width={WIDTH} />
        <rect
          fill="none"
          height={HEIGHT - 16}
          rx={6}
          stroke={CHART_INK.axis}
          width={WIDTH - 16}
          x={8}
          y={8}
        />
        <text
          fill={CHART_INK.muted}
          fontSize={13}
          fontStyle="italic"
          x={18}
          y={28}
        >
          U
        </text>
        <circle
          cx={185}
          cy={138}
          fill={CHART_COLORS[0]}
          fillOpacity={0.14}
          r={95}
          stroke={CHART_COLORS[0]}
          strokeWidth={2.5}
        />
        <circle
          cx={295}
          cy={138}
          fill={CHART_COLORS[3]}
          fillOpacity={0.14}
          r={95}
          stroke={CHART_COLORS[3]}
          strokeWidth={2.5}
        />
        <text
          fill={CHART_COLORS[0]}
          fontSize={15}
          fontWeight={700}
          x={110}
          y={52}
        >
          A
        </text>
        <text
          fill={CHART_COLORS[3]}
          fontSize={15}
          fontWeight={700}
          textAnchor="end"
          x={370}
          y={52}
        >
          B
        </text>
        <Region elements={onlyA} x={135} />
        <Region elements={both} x={240} />
        <Region elements={onlyB} x={345} />
        {showUniverse
          ? lines(outside)
              .slice(0, 2)
              .map((row, i) => (
                <text
                  fill={CHART_INK.text}
                  fontSize={12}
                  key={row}
                  textAnchor="end"
                  x={WIDTH - 18}
                  y={HEIGHT - 36 + i * 15}
                >
                  {row}
                </text>
              ))
          : null}
      </svg>
      <button
        className={BUTTON_SMALL}
        onClick={() => {
          if (ref.current) {
            downloadPng("venn-diagram.png", ref.current).catch(() => undefined);
          }
        }}
        type="button"
      >
        {downloadLabel}
      </button>
    </div>
  );
}
